// ============================================================================
// TEAM DETAIL PAGE
// Detailed view of a single team with member management
// ============================================================================

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  Users,
  UserPlus,
  Settings,
  Crown,
  Mail,
  MoreVertical,
  X,
  Search
} from 'lucide-react';
import { useTeam, useTeamMembers, useAddTeamMember } from '../hooks/useTeams';
import { useAuth } from '../contexts/AuthContext';
import { listUsers, UserProfile } from '../services/userManagement';

export const TeamDetail: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: team, isLoading: teamLoading } = useTeam(teamId!, !!teamId);
  const { data: members, isLoading: membersLoading } = useTeamMembers(teamId!, !!teamId);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [memberRole, setMemberRole] = useState<'member' | 'lead'>('member');
  const addMemberMutation = useAddTeamMember();

  if (teamLoading || !team) {
    return (
      <div style={{ padding: '32px', textAlign: 'center' }}>
        <div className="spinner" />
        <p style={{ color: '#64748b', marginTop: '16px' }}>Loading team...</p>
      </div>
    );
  }

  const isAdmin = user?.role === 'system_admin';
  const isTeamLead = team.teamLead === user?.userId;
  const canManage = isAdmin || isTeamLead;

  // Search for users to add
  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const result = await listUsers({
        search: query,
        status: 'active'
      });
      // Filter out users already in the team
      const existingUserIds = members?.map(m => m.userId) || [];
      const filteredResults = result.users.filter(u => !existingUserIds.includes(u.userId));
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Add member to team
  const handleAddMember = async () => {
    if (!selectedUser || !teamId) return;

    try {
      await addMemberMutation.mutateAsync({
        teamId,
        member: {
          userId: selectedUser.userId,
          userEmail: selectedUser.email,
          displayName: selectedUser.displayName,
          role: memberRole
        }
      });
      // Reset modal state
      setShowAddMemberModal(false);
      setSelectedUser(null);
      setSearchQuery('');
      setSearchResults([]);
      setMemberRole('member');
    } catch (error) {
      console.error('Error adding member:', error);
      alert('Failed to add member. Please try again.');
    }
  };

  // Reset modal when closed
  const handleCloseModal = () => {
    setShowAddMemberModal(false);
    setSelectedUser(null);
    setSearchQuery('');
    setSearchResults([]);
    setMemberRole('member');
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/teams')}
        style={{
          padding: '8px 12px',
          background: 'transparent',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginBottom: '24px',
          color: '#475569'
        }}
      >
        <ChevronLeft size={16} />
        Back to Teams
      </button>

      {/* Team Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'start',
        marginBottom: '32px'
      }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: '#00c6c2',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Users size={32} color="white" />
          </div>
          <div>
            <h1 style={{ fontSize: '32px', fontWeight: '700', margin: '0 0 8px 0' }}>
              {team.teamName}
            </h1>
            {team.description && (
              <p style={{ color: '#64748b', margin: '0 0 12px 0' }}>
                {team.description}
              </p>
            )}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span style={{
                padding: '4px 8px',
                background: '#f0fdf4',
                color: '#166534',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {members?.length || 0} Members
              </span>
              {team.teamLead && (
                <span style={{
                  padding: '4px 8px',
                  background: '#fef3c7',
                  color: '#92400e',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Crown size={12} />
                  {team.teamLeadName || 'Team Lead'}
                </span>
              )}
            </div>
          </div>
        </div>

        {canManage && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setShowAddMemberModal(true)}
              style={{
                padding: '10px 16px',
                background: '#00c6c2',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <UserPlus size={16} />
              Add Member
            </button>
            <button
              onClick={() => navigate(`/teams/${teamId}/settings`)}
              style={{
                padding: '10px',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              <Settings size={16} color="#64748b" />
            </button>
          </div>
        )}
      </div>

      {/* Members List */}
      <div style={{
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
            Team Members
          </h3>
        </div>

        {membersLoading ? (
          <div style={{ padding: '32px', textAlign: 'center' }}>
            <div className="spinner" />
          </div>
        ) : members?.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center' }}>
            <Users size={48} color="#cbd5e1" />
            <p style={{ color: '#64748b', marginTop: '12px' }}>
              No members yet. Add members to get started.
            </p>
          </div>
        ) : (
          <div>
            {members?.map((member, index) => (
              <div
                key={member.userId}
                style={{
                  padding: '16px 20px',
                  borderBottom: index < members.length - 1 ? '1px solid #f1f5f9' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: '#f1f5f9',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#475569'
                  }}>
                    {member.displayName?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#0f172a',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {member.displayName || 'Unknown User'}
                      {member.role === 'lead' && (
                        <Crown size={14} color="#eab308" />
                      )}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <Mail size={12} />
                      {member.userEmail || 'No email'}
                    </div>
                  </div>
                </div>

                {canManage && (
                  <button
                    style={{
                      padding: '8px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    <MoreVertical size={16} color="#64748b" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div
          onClick={handleCloseModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>Add Team Member</h2>
              <button
                onClick={handleCloseModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <X size={20} color="#64748b" />
              </button>
            </div>

            {/* Search Input */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#334155'
              }}>
                Search Users
              </label>
              <div style={{ position: 'relative' }}>
                <Search
                  size={16}
                  color="#94a3b8"
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)'
                  }}
                />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 36px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  autoFocus
                />
              </div>
            </div>

            {/* Search Results */}
            {searchQuery.trim().length >= 2 && (
              <div style={{
                marginBottom: '20px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                {isSearching ? (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <div className="spinner" style={{ margin: '0 auto' }} />
                  </div>
                ) : searchResults.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                    No users found
                  </div>
                ) : (
                  searchResults.map((user) => (
                    <div
                      key={user.userId}
                      onClick={() => setSelectedUser(user)}
                      style={{
                        padding: '12px',
                        borderBottom: '1px solid #f1f5f9',
                        cursor: 'pointer',
                        background: selectedUser?.userId === user.userId ? '#f0fdfa' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                      onMouseOver={(e) => {
                        if (selectedUser?.userId !== user.userId) {
                          e.currentTarget.style.background = '#f8fafc';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (selectedUser?.userId !== user.userId) {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <div style={{
                        width: '36px',
                        height: '36px',
                        background: '#f1f5f9',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#475569'
                      }}>
                        {user.displayName.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>
                          {user.displayName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {user.email}
                        </div>
                      </div>
                      {selectedUser?.userId === user.userId && (
                        <div style={{
                          width: '20px',
                          height: '20px',
                          background: '#00c6c2',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '12px'
                        }}>
                          ✓
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Selected User & Role */}
            {selectedUser && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  padding: '12px',
                  background: '#f0fdfa',
                  border: '1px solid #5eead4',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontSize: '12px', color: '#115e59', fontWeight: '600', marginBottom: '4px' }}>
                    Selected User
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>
                    {selectedUser.displayName}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    {selectedUser.email}
                  </div>
                </div>

                {/* Role Selection */}
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: '#334155'
                }}>
                  Member Role
                </label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => setMemberRole('member')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: memberRole === 'member' ? '#00c6c2' : 'white',
                      color: memberRole === 'member' ? 'white' : '#475569',
                      border: `2px solid ${memberRole === 'member' ? '#00c6c2' : '#e2e8f0'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Member
                  </button>
                  <button
                    onClick={() => setMemberRole('lead')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: memberRole === 'lead' ? '#00c6c2' : 'white',
                      color: memberRole === 'lead' ? 'white' : '#475569',
                      border: `2px solid ${memberRole === 'lead' ? '#00c6c2' : '#e2e8f0'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}
                  >
                    <Crown size={14} />
                    Lead
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCloseModal}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  color: '#475569'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                disabled={!selectedUser || addMemberMutation.isPending}
                style={{
                  padding: '10px 20px',
                  background: selectedUser && !addMemberMutation.isPending ? '#00c6c2' : '#cbd5e1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: selectedUser && !addMemberMutation.isPending ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {addMemberMutation.isPending ? (
                  <>
                    <div className="spinner" style={{ width: '14px', height: '14px' }} />
                    Adding...
                  </>
                ) : (
                  <>
                    <UserPlus size={16} />
                    Add Member
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
